import React, { useState, useEffect } from "react";
import { ReactSortable } from "react-sortablejs";
import axiosInstance from "../../services/axiosService";
import SingleSelectBox from "../FormComponents/SingleSelectBox";
import FieldSelectorFromPopup from "../BuilderComponents/FieldSelectorFromPopup";
import Label from "../FormComponents/Label";
import ButtonActionIcon from "../BuilderComponents/ButtonActionIcon";
import Input from "../FormComponents/Input";

const EditDvGroup = ({ editElementData, setEditElementData, smId, device, tableType = "statemachine" }) => {
  const [selectedDvId, setSelectedDvId] = useState(null);
  const [detailsViewOptions, setDetailsViewOptions] = useState([]);
  const [stateData, setStateData] = useState([]);
  const [loadingDetailsView, setLoadingDetailsView] = useState(false);

  const dvList = editElementData?.dv_list || [];

  const logicOptions = [
    { id: "state_base", name: "State Based" },
    { id: "field_base", name: "Field Based" }
  ];

  const fetchDetailsViewData = async () => {
    setLoadingDetailsView(true);
    try {
      const { data } = await axiosInstance.get("/detailsview");
      const filteredData = data?.[device]?.filter((dv) => dv?.data?.table_id === smId) || [];
      setDetailsViewOptions(filteredData);
    } catch (error) {
      console.error("Error fetching DetailsView data:", error);
    } finally {
      setLoadingDetailsView(false);
    }
  };

  const fetchStateData = async () => {
    try {
      const { data } = await axiosInstance.get(`/state-machine/${smId}/states`);
      setStateData(data);
    } catch (error) {
      console.error("Error fetching States data:", error);
    }
  };

  useEffect(() => {
    if (editElementData?.logic_type === "state_base" && stateData.length === 0) {
      fetchStateData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editElementData?.logic_type]);

  useEffect(() => {
    if (device) {
      fetchDetailsViewData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]);

  const deleteDv = (e, indexToDelete) => {
    e.stopPropagation();
    const updatedDvList = dvList.filter((_, index) => index !== indexToDelete);
    setEditElementData({
      ...editElementData,
      dv_list: updatedDvList
    });
    if (selectedDvId && indexToDelete === selectedDvId) {
      setSelectedDvId(null);
    }
  };

  const handleListOrderChange = (newList) => {
    const cleanedList = newList.map((item) => {
      const cleanItem = { ...item };
      delete cleanItem.chosen;
      delete cleanItem.selected;
      return cleanItem;
    });

    setEditElementData({
      ...editElementData,
      dv_list: cleanedList
    });
  };

  const updateDvValue = (dvIndex, value) => {
    const updatedDvList = dvList.map((dv, index) => {
      if (index === dvIndex) {
        return {
          ...dv,
          logic_data: {
            ...dv.logic_data,
            operator: "eq", // Always eq as specified
            value: value
          }
        };
      }
      return dv;
    });

    setEditElementData({
      ...editElementData,
      dv_list: updatedDvList
    });
  };

  const updateDvId = (dvIndex, newDvId) => {
    const updatedDvList = dvList.map((dv, index) => {
      if (index === dvIndex) {
        return {
          ...dv,
          dv_id: newDvId
        };
      }
      return dv;
    });

    setEditElementData({
      ...editElementData,
      dv_list: updatedDvList
    });
  };

  const updateGlobalFieldId = (field) => {
    setEditElementData({
      ...editElementData,
      field_id: field.id || ""
    });
  };

  if (loadingDetailsView) return <div style={{ width: "500px" }}>Loading...</div>;
  if (!smId) return <p>Add SM in settings to configure</p>;

  return (
    <div className="flex_column gap_2" style={{ width: "500px" }}>
      <div className="d_flex gap_2 align_center">
        <SingleSelectBox
          label="Default Detailview"
          options={detailsViewOptions}
          valueKey="id"
          labelKey="name"
          selectedData={editElementData.default_dv_id || ""}
          onChange={(selectedOption) => {
            setEditElementData({
              ...editElementData,
              default_dv_id: selectedOption?.id || ""
            });
          }}
          placeholder="Select default DetailsView"
          isSearch={true}
        />
      </div>

      <div className="d_flex gap_2 align_center">
        <SingleSelectBox
          label="Logic Type"
          options={logicOptions}
          valueKey="id"
          labelKey="name"
          selectedData={editElementData.logic_type || ""}
          onChange={(selectedOption) => {
            setEditElementData({
              ...editElementData,
              logic_type: selectedOption?.id || "",
              dv_list: [],
              field_id: ""
            });
          }}
          placeholder="Select logic type"
          isSearch={true}
          error={!editElementData.logic_type ? "Please select a logic type" : ""}
        />
      </div>

      {editElementData.logic_type === "field_base" && (
        <div>
          <Label className="pb_2">Field ID</Label>
          <FieldSelectorFromPopup
            label="Select Field"
            tableId={smId}
            tableType={tableType}
            fieldId={editElementData.field_id || ""}
            onSelect={updateGlobalFieldId}
            id="global_field_selector"
          />
        </div>
      )}

      <div className="border p_2 border_radius_medium">
        <div className="d_flex gap_2 py_2 justify_between">
          <h3 className="font_weight_semibold">DV Mappings</h3>
          <ButtonActionIcon
            onClick={() => {
              const newDv = {
                dv_id: "",
                logic_data: {}
              };
              const updatedDvList = [...dvList, newDv];
              setEditElementData({
                ...editElementData,
                dv_list: updatedDvList
              });
            }}
            type="add"
          />
        </div>

        <ReactSortable list={dvList} setList={handleListOrderChange} className="flex_column gap_2" animation={200}>
          {dvList.map((dv, index) => {
            return (
              <div key={index} className="p-2 border flex_column gap_2">
                <div className="d_flex gap_2 justify_between w_100">
                  <div className="flex_1 w_50">
                    {editElementData?.logic_type === "state_base" ? (
                      <SingleSelectBox
                        label="State Value"
                        options={stateData || []}
                        valueKey="id"
                        labelKey="name"
                        selectedData={dv.logic_data?.value || ""}
                        onChange={(selectedOption) => {
                          updateDvValue(index, selectedOption?.id || "");
                        }}
                        placeholder="Select state"
                        isSearch={true}
                      />
                    ) : (
                      <div className="flex_column justify_between">
                        <Label>Field Value</Label>
                        <Input
                          type="text"
                          value={dv.logic_data?.value || ""}
                          onChange={(e) => updateDvValue(index, e.target.value)}
                          placeholder="Enter value"
                          inputBoxStyle={{ height: "36px" }}
                          containerStyle={{ paddingTop: "8px" }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex_1 w_50">
                    <SingleSelectBox
                      label="DetailsView"
                      options={detailsViewOptions}
                      valueKey="id"
                      labelKey="name"
                      selectedData={dv.dv_id || ""}
                      onChange={(selectedOption) => {
                        updateDvId(index, selectedOption?.id || "");
                      }}
                      placeholder="Select DetailsView"
                      isSearch={true}
                    />
                  </div>

                  <ButtonActionIcon onClick={(e) => deleteDv(e, index)} type="delete" className="mt_auto mb_2" />
                </div>
              </div>
            );
          })}
        </ReactSortable>

        {dvList.length === 0 && (
          <div className="text_center font_style_italic p_2">Add a DetailsView to get started.</div>
        )}
      </div>
    </div>
  );
};

export default EditDvGroup;
